import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import ApiConfig from "src/apiconfig/apiconfig";
import {
  DEFAULT_MEDIA_CENTER_LAYOUT,
  MEDIA_CENTER_PAGE_KEY,
} from "src/views/pages/MediaCenter/mediaCenterLayout";
import { DEFAULT_SERVICES_LAYOUT, SERVICES_PAGE_KEY } from "src/views/pages/static/servicesLayout";
import { CREATORS_PAGE_KEY, DEFAULT_CREATORS_LAYOUT } from "src/views/pages/Creators/creatorsLayout";
import { CONTACT_PAGE_KEY, DEFAULT_CONTACT_LAYOUT } from "src/views/pages/static/contactLayout";
import {
  DEFAULT_OPPORTUNITIES_LAYOUT,
  OPPORTUNITIES_PAGE_KEY,
} from "src/views/pages/Opportunities/opportunitiesLayout";
import {
  DEFAULT_DISPLAY_PROFILE_LAYOUT,
  DISPLAY_PROFILE_PAGE_KEY,
} from "src/views/pages/static/displayProfileLayout";

export const BUILDER_MESSAGE_SOURCE = "kuvuma-page-builder";
export const HOME_CLIENT_PAGE_KEY = "home_client";
export const HOME_CREATOR_PAGE_KEY = "home_creator";

const cacheKey = (pageKey) => `homeLayout:${pageKey}`;
const FALLBACK_HEADING_FONT = "Clash Display";
const FALLBACK_BODY_FONT = "Poppins";

const ADMIN_ORIGIN = (() => {
  try {
    return process.env.REACT_APP_ADMIN_URL
      ? new URL(process.env.REACT_APP_ADMIN_URL).origin
      : "";
  } catch (e) {
    return "";
  }
})();

// Keep in sync with web_admin/src/views/pages/PageBuilder/sectionSchemas.js.
export const DEFAULT_CLIENT_LAYOUT = {
  theme: {
    headingFont: FALLBACK_HEADING_FONT,
    bodyFont: FALLBACK_BODY_FONT,
    primaryColor: "#FF7F26",
    darkColor: "#010824",
  },
  sections: [
    {
      id: "hero",
      type: "clientHero",
      visible: true,
      style: {},
      props: {
        badge: "Creative Talent Marketplace",
        headline: "Find your creator. Bring ideas to life.",
        headlineAccent: "creator",
        subtext:
          "Discover talented creators, explore their work and services, and connect with the right professional for your next project.",
        searchPlaceholder: "Search creators",
        searchButton: "Search",
        popularLabel: "Popular:",
        popularTags: ["Translation", "Trimming", "Voice-over", "Scripting", "Editing"],
        image: "images/client_main_slider.png",
        imageAlt: "Find creators on KUVUMA",
      },
    },
    {
      id: "stats",
      type: "clientStats",
      visible: true,
      style: {},
      props: {
        creatorsLabel: "Approved creators",
        servicesLabel: "Services",
        projectsLabel: "Projects",
        ratingLabel: "Average rating",
      },
    },
    {
      id: "categories",
      type: "popularCategories",
      visible: true,
      style: {},
      props: {
        title: "Explore popular categories",
        subtitle: "Find the right talent across every domain.",
        linkLabel: "Browse creator",
      },
    },
    {
      id: "howItWorks",
      type: "howItWorks",
      visible: true,
      style: {},
      props: {
        title: "Find the right creator in 3 simple steps",
        titleAccent: "3 simple steps",
        subtitle: "",
        steps: [
          {
            title: "Browse creators and services",
            text: "Search profiles and portfolios to find creatives who match your brief, skills and budget.",
            image: "images/client_how_browse.png",
          },
          {
            title: "Compare and connect",
            text: "Review ratings, experience and pricing, then shortlist and reach out to the right talent.",
            image: "images/client_how_compare.png",
          },
          {
            title: "Bring your project to life",
            text: "Collaborate with your creator, track delivery and receive polished work when it is ready.",
            image: "images/client_how_deliver.png",
          },
        ],
        primaryButton: "Browse Creators",
        secondaryButton: "Post an Opportunity",
        showActions: true,
      },
    },
    {
      id: "ads",
      type: "landingAdvertisement",
      visible: true,
      style: {},
      props: {},
    },
    {
      id: "marketplace",
      type: "twoSidedMarketplace",
      visible: true,
      style: {},
      props: {
        heading: "Looking to hire creative talent?",
        text: "Search the KUVUMA talent database, shortlist creatives and work with them directly.",
        cardTitle: "Need Creative Talent?",
        cardLead: "Find the right creative professional for your project.",
        searchLabel: "Search by",
        chips: ["Skills", "Experience", "Location", "Availability", "Budget/rate"],
        tagline: "Shortlist talent. Contact them. Collaborate.",
        buttonLabel: "Find Creative Talent",
      },
    },
    {
      id: "startUsing",
      type: "startUsingKuvuma",
      visible: true,
      style: {},
      props: {
        title: "Start using KUVUMA",
        subtitle: "to reach people, create publicity or generate revenue",
        intro:
          "Brands, businesses and organisations use KUVUMA to find and hire African creative talent, order creative services and buy ready-made video and audio content. Post a job, gig or audition and let creatives apply, or search the talent database and contact them directly.",
        cards: [
          {
            title: "FIND & HIRE TALENT",
            description:
              "Post a job, gig or audition and let African creatives apply, or search the talent database and contact them directly.",
            img: "",
          },
          {
            title: "ORDER SERVICES",
            description:
              "Create a task and assign to our creative experts: writers, editors, developers, translators, transcribers, designers, advisors, trainers, influencers.",
            img: "",
          },
          {
            title: "BUY MEDIA",
            description:
              "Browse and buy ready-made video and audio content to publicise your products, services and campaigns.",
            img: "",
          },
        ],
      },
    },
    {
      id: "plans",
      type: "selectYourPlan",
      visible: true,
      style: {},
      props: {
        heading: "UPGRADE YOUR ACCOUNT",
        subheading: "“Subscribe Your Role & Login To Your Dashboard.\"",
        bgImage: "images/plans_back_new.png",
      },
    },
    {
      id: "slider",
      type: "lowerSlideBar",
      visible: true,
      style: {},
      props: {},
    },
  ],
};

// Keep in sync with web_admin/src/views/pages/PageBuilder/sectionSchemas.js.
export const DEFAULT_CREATOR_LAYOUT = {
  theme: { ...DEFAULT_CLIENT_LAYOUT.theme },
  sections: [
    {
      id: "hero",
      type: "creatorHero",
      visible: true,
      style: {},
      props: {
        badge: "For Creators",
        headline: "Turn your creativity into opportunity.",
        headlineAccent: "opportunity.",
        subtext:
          "Showcase your talent, list your services, and connect with clients looking for skills like yours on KUVUMA.",
        primaryButton: "Join as a Creator",
        secondaryButton: "Explore Creator Benefits",
      },
    },
    {
      id: "whyJoin",
      type: "creatorWhyJoin",
      visible: true,
      style: {},
      props: {
        title: "Why join as a creator?",
        subtitle: "Get the tools, exposure and opportunities to grow your creative career.",
        cards: [
          {
            title: "Showcase your work",
            text: "Build a professional profile with your portfolio, show reels and services.",
          },
          {
            title: "Get discovered",
            text: "Connect with clients looking for creative talent across different industries.",
          },
          {
            title: "Earn with your skills",
            text: "Find new projects, set your own rates and grow your income.",
          },
          {
            title: "Build your reputation",
            text: "Get reviews, ratings and repeat clients as you complete more projects.",
          },
        ],
      },
    },
    {
      id: "steps",
      type: "howItWorks",
      visible: true,
      style: {},
      props: {
        title: "Start creating opportunities in 3 simple steps",
        titleAccent: "3 simple steps",
        subtitle: "Create your profile, list your services, and start getting project requests.",
        steps: [
          {
            title: "Create your profile",
            text: "Add your skills, portfolio and set up your services in a few minutes.",
            image: "images/creator_how_profile.png",
          },
          {
            title: "List your services",
            text: "Choose what you offer, set your pricing and showcase your work samples.",
            image: "images/creator_how_services.png",
          },
          {
            title: "Get project requests",
            text: "Receive messages from clients, discuss projects and start working.",
            image: "images/creator_how_requests.png",
          },
        ],
        primaryButton: "",
        secondaryButton: "",
        showActions: false,
      },
    },
    {
      id: "ads",
      type: "landingAdvertisement",
      visible: true,
      style: {},
      props: {},
    },
    {
      id: "marketplace",
      type: "creativesMarketplace",
      visible: true,
      style: {},
      props: {
        label: "For Creatives",
        heading: "Ready to get your work seen?",
        text: "Build your profile, showcase your portfolio and get discovered by the people who hire.",
        cardTitle: "Your Talent Deserves to Be Found",
        cardLead: "A place to show what you do and get connected to the people who need it.",
        bullets: [
          "Build a professional creative profile.",
          "Showcase your portfolio.",
          "Discover relevant jobs and projects.",
          "Apply for opportunities.",
          "Get discovered by organisations looking for your skills.",
          "Build your professional reputation.",
        ],
        buttonLabel: "Create Your Free Profile",
      },
    },
    {
      id: "howKuvumaWorks",
      type: "howKuvumaWorks",
      visible: true,
      style: {},
      props: {
        label: "How it works",
        title: "How KUVUMA Works",
        subtitle: "From your first profile to your next big project, in seven steps.",
        steps: [
          { title: "Create Your Profile", text: "Tell the world what you do." },
          {
            title: "Showcase Your Work",
            text: "Upload your strongest creative projects, in whatever format suits your discipline.",
          },
          {
            title: "Discover Opportunities",
            text: "Browse jobs, gigs, contracts, collaborations, auditions, internships and other creative opportunities.",
          },
          { title: "Apply", text: "Submit your profile and relevant work directly to opportunities." },
          {
            title: "Get Discovered",
            text: "Organisations search the KUVUMA talent database to find and contact creatives like you.",
          },
          {
            title: "Get Hired",
            text: "Move from an application or enquiry to an actual project or engagement.",
          },
          {
            title: "Build Your Reputation",
            text: "Completed projects contribute to your professional track record.",
          },
        ],
        primaryButton: "Create Your Free Profile",
        secondaryButton: "Browse Opportunities",
      },
    },
    {
      id: "startUsing",
      type: "startUsingKuvuma",
      visible: true,
      style: {},
      props: {
        title: "Start using KUVUMA",
        subtitle: "to reach people, create publicity or generate revenue",
        intro:
          "Organisations use KUVUMA for training, mentorship, audi-visual manuals, and awareness creation. Businesses use KUVUMA to distribute content to publicize their products and services. Organisations use KUVUMA to send custom-made audio clips to enduser feature phones, in target locations, for access by recipients without internet. Trainers use KUVUMA to distribute content to trainees, share, monitor video and audio content, and to assess impact of use.",
        cards: [
          {
            title: "CREATE & EDIT",
            description:
              "Use our advanced studio tools, footage and images to conceptualise, create, edit and publish your videos, podcasts, slides and presentations.",
            img: "",
          },
          {
            title: "ORDER SERVICES",
            description:
              "Create a task and assign to our creative experts: writers, editors, developers, translators, transcribers, designers, advisors, trainers, influencers.",
            img: "",
          },
          {
            title: "LEARN & GROW",
            description:
              "Access our multi-media library and get value content to grow your enterprise, develop yourself, tackle mental health, discover your life purpose, share.",
            img: "",
          },
        ],
      },
    },
    {
      id: "plans",
      type: "selectYourPlan",
      visible: true,
      style: {},
      props: {
        heading: "UPGRADE YOUR ACCOUNT",
        subheading: "“Subscribe Your Role & Login To Your Dashboard.\"",
        bgImage: "images/plans_back_new.png",
      },
    },
    {
      id: "slider",
      type: "lowerSlideBar",
      visible: true,
      style: {},
      props: {},
    },
  ],
};

export const PAGE_LAYOUT_DEFAULTS = {
  [HOME_CLIENT_PAGE_KEY]: DEFAULT_CLIENT_LAYOUT,
  [HOME_CREATOR_PAGE_KEY]: DEFAULT_CREATOR_LAYOUT,
  [MEDIA_CENTER_PAGE_KEY]: DEFAULT_MEDIA_CENTER_LAYOUT,
  [SERVICES_PAGE_KEY]: DEFAULT_SERVICES_LAYOUT,
  [CREATORS_PAGE_KEY]: DEFAULT_CREATORS_LAYOUT,
  [CONTACT_PAGE_KEY]: DEFAULT_CONTACT_LAYOUT,
  [OPPORTUNITIES_PAGE_KEY]: DEFAULT_OPPORTUNITIES_LAYOUT,
  [DISPLAY_PROFILE_PAGE_KEY]: DEFAULT_DISPLAY_PROFILE_LAYOUT,
};

const defaultsFor = (pageKey) => PAGE_LAYOUT_DEFAULTS[pageKey] || DEFAULT_CLIENT_LAYOUT;

const CREATOR_MODE_PAGE_KEYS = [HOME_CREATOR_PAGE_KEY, OPPORTUNITIES_PAGE_KEY, DISPLAY_PROFILE_PAGE_KEY];

/** Site mode a builder page is shown in ("CREATOR" or "CLIENT"). */
export const pageModeFor = (pageKey) =>
  CREATOR_MODE_PAGE_KEYS.indexOf(pageKey) !== -1 ? "CREATOR" : "CLIENT";

const isPlainObject = (value) =>
  !!value && typeof value === "object" && !Array.isArray(value);

/** Lays a saved config over the defaults: saved order first, then any section types the config doesn't know yet. */
export const mergeLayout = (config, defaults = DEFAULT_CLIENT_LAYOUT) => {
  if (!isPlainObject(config) || !Array.isArray(config.sections)) return defaults;
  const defaultsByType = {};
  defaults.sections.forEach((section) => {
    defaultsByType[section.type] = section;
  });
  const seen = new Set();
  const sections = [];
  config.sections.forEach((section) => {
    const base = section && defaultsByType[section.type];
    if (!base || seen.has(section.type)) return;
    seen.add(section.type);
    sections.push({
      ...base,
      id: section.id || base.id,
      visible: section.visible !== false,
      style: isPlainObject(section.style) ? section.style : {},
      props: { ...base.props, ...(isPlainObject(section.props) ? section.props : {}) },
    });
  });
  defaults.sections.forEach((section) => {
    if (!seen.has(section.type)) sections.push(section);
  });
  return {
    theme: { ...defaults.theme, ...(isPlainObject(config.theme) ? config.theme : {}) },
    sections,
  };
};

const fontStack = (name) =>
  `'${name}', ${name === FALLBACK_BODY_FONT ? "" : "Poppins, "}sans-serif`;

// Only values the admin changed become variables, so every section keeps its original fallback look by default.
export const themeCssVars = (theme = {}, defaults = DEFAULT_CLIENT_LAYOUT.theme) => {
  const vars = {};
  if (theme.headingFont && theme.headingFont !== defaults.headingFont) {
    vars["--hp-heading-font"] = fontStack(theme.headingFont);
  }
  if (theme.bodyFont && theme.bodyFont !== defaults.bodyFont) {
    vars["--hp-body-font"] = fontStack(theme.bodyFont);
  }
  const changed = (key) =>
    !!theme[key] && theme[key].toLowerCase() !== String(defaults[key] || "").toLowerCase();
  if (changed("primaryColor")) vars["--hp-primary"] = theme.primaryColor;
  if (changed("darkColor")) vars["--hp-dark"] = theme.darkColor;
  return vars;
};

export const sectionCssVars = (style = {}) => {
  const vars = {};
  if (style.headingFont) vars["--hp-heading-font"] = fontStack(style.headingFont);
  if (style.bodyFont) vars["--hp-body-font"] = fontStack(style.bodyFont);
  if (style.accentColor) vars["--hp-primary"] = style.accentColor;
  if (style.bgColor) vars["--hp-section-bg"] = style.bgColor;
  if (style.textColor) vars["--hp-section-text"] = style.textColor;
  return vars;
};

export const isBuilderPreview = () => {
  if (typeof window === "undefined" || window.parent === window) return false;
  return new URLSearchParams(window.location.search).get("builderPreview") === "1";
};

/** Page the admin builder is editing (from `?builderPage=`), or null outside the builder iframe. */
export const builderPreviewPage = () => {
  if (!isBuilderPreview()) return null;
  const page = new URLSearchParams(window.location.search).get("builderPage");
  return PAGE_LAYOUT_DEFAULTS[page] ? page : HOME_CLIENT_PAGE_KEY;
};

const readCachedLayout = (pageKey) => {
  try {
    const raw = window.localStorage.getItem(cacheKey(pageKey));
    return raw ? mergeLayout(JSON.parse(raw), defaultsFor(pageKey)) : null;
  } catch (e) {
    return null;
  }
};

const postToBuilder = (message) => {
  if (!ADMIN_ORIGIN) return;
  window.parent.postMessage({ source: BUILDER_MESSAGE_SOURCE, ...message }, ADMIN_ORIGIN);
};

/**
 * Layout for one builder page. Public visitors get the published config (cached for instant
 * repeat visits); inside the admin page builder iframe the layout is driven by postMessage instead.
 * Mount with `key={pageKey}` so switching pages starts from that page's own state.
 * With `enabled: false` nothing is fetched and the page renders its built-in defaults.
 */
export function usePageLayout(pageKey = HOME_CLIENT_PAGE_KEY, { enabled = true } = {}) {
  const previewRef = useRef(isBuilderPreview());
  const preview = previewRef.current;
  const defaults = defaultsFor(pageKey);
  const [layout, setLayout] = useState(() =>
    preview ? defaults : readCachedLayout(pageKey) || defaults
  );
  const [ready, setReady] = useState(() => preview || !!readCachedLayout(pageKey));
  const [selectedId, setSelectedId] = useState(null);

  useEffect(() => {
    if (preview || !enabled) return undefined;
    let cancelled = false;
    axios
      .get(ApiConfig.publishedPageLayout, { params: { pageKey } })
      .then((res) => {
        if (cancelled) return;
        const config = res?.data?.responseCode === 200 ? res?.data?.result?.config : null;
        if (config) {
          window.localStorage.setItem(cacheKey(pageKey), JSON.stringify(config));
        } else {
          window.localStorage.removeItem(cacheKey(pageKey));
        }
        setLayout(config ? mergeLayout(config, defaultsFor(pageKey)) : defaultsFor(pageKey));
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setReady(true);
      });
    return () => {
      cancelled = true;
    };
  }, [preview, pageKey, enabled]);

  useEffect(() => {
    if (!preview || !ADMIN_ORIGIN) return undefined;
    const onMessage = (event) => {
      if (event.origin !== ADMIN_ORIGIN) return;
      const data = event.data;
      if (!data || data.source !== BUILDER_MESSAGE_SOURCE) return;
      if (data.pageKey && data.pageKey !== pageKey) return;
      if (data.type === "config") {
        setLayout(mergeLayout(data.config, defaultsFor(pageKey)));
        if (data.selectedId !== undefined) setSelectedId(data.selectedId);
      } else if (data.type === "select") {
        setSelectedId(data.sectionId || null);
        if (data.sectionId && data.scroll) {
          const node = document.querySelector(`[data-builder-section="${data.sectionId}"]`);
          if (node) node.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      }
    };
    window.addEventListener("message", onMessage);
    postToBuilder({ type: "ready", pageKey });
    return () => window.removeEventListener("message", onMessage);
  }, [preview, pageKey]);

  const selectSection = useCallback(
    (sectionId) => {
      if (!preview) return;
      setSelectedId(sectionId);
      postToBuilder({ type: "select", sectionId });
    },
    [preview]
  );

  if (!enabled && !preview) {
    return { layout: defaults, defaults, ready: true, preview, selectedId: null, selectSection };
  }
  return { layout, defaults, ready, preview, selectedId, selectSection };
}
